import { useEffect, useRef, useState } from "react";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { Box, createStyles, Table, Text, TextInput } from "@mantine/core";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { IconChevronRight, IconPlus, IconSearch } from "@tabler/icons-react";
import ServiceRequestNewDrawer from "../ServiceRequestNewDrawer";
import ServiceRequestDetailDrawer from "../ServiceRequestDetailDrawer";
import { STATUS_LABELS } from "../ServiceRequestsTable";
import { getStaffMyRequests, getStaffMyRequestsCounts } from "../../services/serviceRequests";

// Mirrors the admin Backlog/Board page's own useRemainingHeight: the list
// card's top is fixed by the "New Request" toolbar above it, so the only
// unknown is how far down the viewport still runs from there — filling that
// in keeps this list's bottom edge on the viewport edge the same way the
// admin's Backlog/Board section does, instead of stopping short and leaving
// bare page background below a short list.
const useRemainingHeight = (bottomGap, deps) => {
  const ref = useRef(null);
  const [height, setHeight] = useState(null);
  useEffect(() => {
    const measure = () => {
      if (!ref.current) return;
      setHeight(Math.max(240, window.innerHeight - ref.current.getBoundingClientRect().top - bottomGap));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return [ref, height];
};

const useStyles = createStyles(() => ({
  toolbar: {
    display: "flex",
    padding: "10px 12px",
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    marginBottom: 18,
  },
  addBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    height: 40,
    padding: "0 16px",
    border: 0,
    borderRadius: 8,
    backgroundColor: "var(--app-primary)",
    color: "var(--app-on-primary)",
    fontFamily: "Inter",
    fontSize: 13.5,
    fontWeight: 700,
    cursor: "pointer",
    "&:hover": { filter: "brightness(0.94)" },
  },
  listSection: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    overflow: "hidden",
  },
  tableScroll: { overflowX: "auto" },
  listToolbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "14px 16px",
    borderBottom: "1px solid var(--app-border)",
    flexWrap: "wrap",
  },
  segView: {
    display: "inline-flex",
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 10,
    padding: 4,
    gap: 4,
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
  },
  segBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: 0,
    background: "transparent",
    borderRadius: 7,
    padding: "8px 14px",
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 600,
    color: "var(--app-text-muted)",
    cursor: "pointer",
    "&:hover": { color: "var(--app-text)" },
  },
  segBtnOn: { background: "var(--app-text)", color: "var(--app-surface)", "&:hover": { color: "var(--app-surface)" } },
  searchInput: { flex: 1, minWidth: 200 },
  searchInputControl: {
    height: 40,
    borderRadius: 8,
    fontFamily: "Inter",
    fontSize: 13.5,
    borderColor: "var(--app-border)",
    background: "var(--app-surface)",
    color: "var(--app-text)",
    "&::placeholder": { color: "var(--app-text-muted)" },
    "&:focus": { borderColor: "var(--app-text)" },
  },
  searchIcon: { color: "var(--app-text-muted)" },
  table: {
    minWidth: 820,
    backgroundColor: "var(--app-surface) !important",
    fontFamily: "Inter !important",
    "& thead tr": { background: "var(--app-surface-2) !important" },
    "& thead th": {
      color: "var(--app-text) !important",
      fontFamily: "Inter !important",
      fontSize: "12.5px !important",
      fontWeight: "600 !important",
      padding: "14px 16px !important",
      textAlign: "left",
      whiteSpace: "nowrap",
      borderBottom: "1px solid var(--app-border) !important",
    },
    "& tbody td": {
      fontFamily: "Inter !important",
      padding: "14px 16px !important",
      borderBottom: "1px solid var(--app-border) !important",
      borderRight: "none !important",
      whiteSpace: "nowrap",
    },
    "& tbody tr:hover": { backgroundColor: "var(--app-surface-2) !important", cursor: "pointer" },
    "& tbody tr:last-child td": { borderBottom: "none !important" },
  },
  statusPill: {
    display: "inline-flex", alignItems: "center", fontSize: 11, fontWeight: 700, letterSpacing: "0.05em",
    textTransform: "uppercase", padding: "4px 12px", borderRadius: 999, border: "1px solid var(--app-border)",
    background: "var(--app-surface-2)", color: "var(--app-text)", whiteSpace: "nowrap",
  },
  chevron: { color: "var(--app-text-muted)" },
  chevronCol: { width: 40 },
  rowEmpty: { textAlign: "center", color: "var(--app-text-muted)", fontSize: 12.5, padding: "40px 16px !important", cursor: "default" },

  // ── phone layout ──
  // Same reasoning as the admin backlog table (ServiceRequestsTable): a 7-column
  // 820px table can only scroll sideways on a phone, so each request becomes a
  // card carrying the same fields.
  mList: { display: "flex", flexDirection: "column", gap: 10, padding: 12 },
  mCard: {
    border: "1px solid var(--app-border)",
    borderRadius: 10,
    background: "var(--app-surface)",
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    cursor: "pointer",
    "&:active": { background: "var(--app-surface-2)" },
  },
  mCardTop: { display: "flex", alignItems: "center", gap: 8 },
  mCardId: { fontSize: 12, fontWeight: 600, color: "var(--app-text-muted)", fontVariantNumeric: "tabular-nums" },
  mCardTopRight: { marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 },
  mCardTitle: { fontSize: 14, fontWeight: 600, color: "var(--app-text)", lineHeight: 1.35 },
  mCardMeta: { display: "flex", flexWrap: "wrap", gap: 6 },
  mChip: {
    fontSize: 11,
    fontWeight: 600,
    color: "var(--app-text-muted)",
    background: "var(--app-surface-2)",
    border: "1px solid var(--app-border)",
    borderRadius: 6,
    padding: "2px 8px",
  },
  mCardWhen: {
    fontSize: 12,
    color: "var(--app-text-muted)",
    paddingTop: 8,
    borderTop: "1px solid var(--app-border-soft, var(--app-border))",
  },
  mEmpty: { fontSize: 12.5, color: "var(--app-text-muted)", textAlign: "center", padding: "28px 8px" },
  toolbarMobile: { "@media (max-width: 560px)": { "& > button": { width: "100%", justifyContent: "center" } } },
  listToolbarMobile: { "@media (max-width: 560px)": { alignItems: "stretch", flexDirection: "column" } },
  segViewMobile: { "@media (max-width: 560px)": { display: "flex", "& > button": { flex: 1, justifyContent: "center" } } },
}));

const displayRef = (val) => {
  if (!val) return "—";
  if (typeof val === "string") return val;
  return val.name || val.title || [val.firstName, val.lastName].filter(Boolean).join(" ") || val.id || "—";
};

const matchesSearch = (r, q) => {
  if (!q) return true;
  const hay = `${r.requestNumber || ""} ${r.title || ""} ${displayRef(r.template)} ${displayRef(r.facility)}`.toLowerCase();
  return hay.includes(q.toLowerCase());
};

// Staff only ever see requests they submitted or are assigned to, split
// into Active / Completed —
// mirrors the mockup's v6 "Staff view". The detail drawer is reused as-is:
// approve/reject/edit/status-change are admin-gated, and the internal
// Comments/History activity section is separately hidden for plain staff
// accounts (role: "staff", type: "staff" — see isPlainStaff in
// ServiceRequestDetailDrawer), so a staff member opening it sees the same
// Request Information + Details layout with none of the internal-only parts.
const ServiceRequestsStaffView = () => {
  const { classes, cx } = useStyles();
  // Resolved on the first render, not in an effect — see ServiceRequestsTable.
  const isMobile = useMediaQuery("(max-width: 768px)", undefined, { getInitialValueInEffect: false });
  const location = useLocation();
  const openedFromNotificationRef = useRef(null);
  const { staffRequests } = useSelector((state) => state.serviceRequests);
  const [tab, setTab] = useState("active");
  const [search, setSearch] = useState("");
  const [newOpened, { open: openNew, close: closeNew }] = useDisclosure(false);
  // 40px matches the page's own bottom padding (ServiceRequests/index.jsx's
  // `.view`), so the list's bottom edge lands on the viewport edge instead
  // of forcing a few more pixels of page scroll to reach that padding.
  const [fillRef, fillHeight] = useRemainingHeight(40, [isMobile]);
  const [detailRequestId, setDetailRequestId] = useState(null);
  const [detailOpened, { open: openDetail, close: closeDetail }] = useDisclosure(false);

  useEffect(() => {
    getStaffMyRequestsCounts();
  }, []);

  useEffect(() => {
    getStaffMyRequests(tab);
  }, [tab]);

  // Landing here from a notification click carries the target request's id
  // in router state — open its detail drawer once. A ref (not a re-navigate
  // to clear the state) guards against reopening on every re-render.
  useEffect(() => {
    const id = location.state?.serviceRequestId;
    if (!id || openedFromNotificationRef.current === id) return;
    openedFromNotificationRef.current = id;
    setDetailRequestId(id);
    openDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const filtered = (staffRequests || []).filter((r) => matchesSearch(r, search));

  // A brand-new request is always active, so land on that tab — otherwise a
  // submitter sitting on Completed would file a request and see nothing. When
  // the tab is already Active the effect won't re-run, so refetch by hand.
  const handleCreated = () => {
    if (tab === "active") getStaffMyRequests("active");
    else setTab("active");
    getStaffMyRequestsCounts();
  };

  const handleView = (row) => {
    setDetailRequestId(row.id);
    openDetail();
  };

  return (
    <Box>
      {newOpened && (
        <ServiceRequestNewDrawer
          opened={newOpened}
          close={closeNew}
          onCreated={() => {
            getStaffMyRequests(tab);
            getStaffMyRequestsCounts();
          }}
        />
      )}
      <ServiceRequestDetailDrawer opened={detailOpened} requestId={detailRequestId} close={closeDetail} />

      <Box className={cx(classes.toolbar, classes.toolbarMobile)}>
        <button type="button" className={classes.addBtn} onClick={openNew}>
          <IconPlus size={17} stroke={2.5} />New Request
        </button>
      </Box>

      <Box ref={fillRef} className={classes.listSection} style={{ minHeight: fillHeight }}>
        <Box className={cx(classes.listToolbar, classes.listToolbarMobile)}>
          <Box className={cx(classes.segView, classes.segViewMobile)}>
            <button type="button" className={cx(classes.segBtn, tab === "active" && classes.segBtnOn)} onClick={() => setTab("active")}>
              Active
            </button>
            <button type="button" className={cx(classes.segBtn, tab === "completed" && classes.segBtnOn)} onClick={() => setTab("completed")}>
              Completed
            </button>
          </Box>
          <TextInput
            icon={<IconSearch size={16} />}
            placeholder="Search my requests…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            className={classes.searchInput}
            classNames={{ input: classes.searchInputControl, icon: classes.searchIcon }}
          />
        </Box>

        {isMobile ? (
          <Box className={classes.mList}>
            {filtered.length ? filtered.map((r) => (
              <Box key={r.id} className={classes.mCard} onClick={() => handleView(r)}>
                <Box className={classes.mCardTop}>
                  <Text className={classes.mCardId}>{r.requestNumber || r.id}</Text>
                  <Box className={classes.mCardTopRight}>
                    <span className={classes.statusPill}>{STATUS_LABELS[r.status] || r.status}</span>
                    <IconChevronRight size={16} className={classes.chevron} />
                  </Box>
                </Box>
                <Text className={classes.mCardTitle}>{r.title}</Text>
                <Box className={classes.mCardMeta}>
                  <span className={classes.mChip}>{displayRef(r.template)}</span>
                  <span className={classes.mChip}>{displayRef(r.facility)}</span>
                </Box>
                <Text className={classes.mCardWhen}>
                  Submitted {r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : "—"}
                </Text>
              </Box>
            )) : (
              <Text className={classes.mEmpty}>You have no requests assigned to you or submitted by you yet.</Text>
            )}
          </Box>
        ) : (
        <Box className={classes.tableScroll}>
          <Table className={classes.table} fontSize="sm" verticalSpacing="md">
            <thead>
              <tr>
                <th>ID</th><th>Request</th><th>Type</th><th>Facility</th><th>Submitted</th><th>Status</th><th className={classes.chevronCol} />
              </tr>
            </thead>
            <tbody>
              {filtered.length ? (
                filtered.map((r) => (
                  <tr key={r.id} onClick={() => handleView(r)}>
                    <td><Text fz={12} fw={600} c="var(--app-text-muted)" ff="Inter">{r.requestNumber || r.id}</Text></td>
                    <td><Text fz={13.5} fw={600} c="var(--app-text)" ff="Inter">{r.title}</Text></td>
                    <td><Text fz={12} c="var(--app-text-muted)" ff="Inter">{displayRef(r.template)}</Text></td>
                    <td><Text fz={13.5} ff="Inter">{displayRef(r.facility)}</Text></td>
                    <td><Text fz={12} c="var(--app-text-muted)" ff="Inter">{r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : "—"}</Text></td>
                    <td><span className={classes.statusPill}>{STATUS_LABELS[r.status] || r.status}</span></td>
                    <td><IconChevronRight size={16} className={classes.chevron} /></td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className={classes.rowEmpty}>You have no requests assigned to you or submitted by you yet.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Box>
        )}
      </Box>
    </Box>
  );
};

export default ServiceRequestsStaffView;
